import type { SpatialTreeItem } from '@thatopen/fragments'

/** A contenção manda na árvore espacial; as referências aparecem à parte.
 *
 *  O importador do Fragments monta a estrutura espacial também com o
 *  IfcRelReferencedInSpatialStructure (por causa dos alinhamentos do
 *  IFC 4.3), e cada elemento só tem um pai. Uma parede contida no piso e
 *  referenciada num espaço podia sair do piso e aparecer só dentro do
 *  espaço, como se lá estivesse contida.
 *
 *  Aqui cada elemento volta ao contentor que o contém
 *  (IfcRelContainedInSpatialStructure) e cada contentor guarda à parte
 *  a lista do que referencia, para a árvore a mostrar marcada. Um
 *  elemento só referenciado (sem contentor, como uma IfcSpatialZone
 *  referenciada no piso) sai da árvore principal e fica só nessa lista. */

export interface Referenciado {
  localId: number
  category: string | null
}

export interface ArvoreComReferencias {
  raiz: SpatialTreeItem
  /** Por contentor (localId): os elementos que ele referencia. */
  referenciados: Map<number, Referenciado[]>
}

interface Posicao {
  no: SpatialTreeItem
  /** O nó cuja lista `children` o contém. */
  pai: SpatialTreeItem
  /** O contentor real mais próximo (nó com localId) acima dele. */
  contentor: number | null
  /** A categoria do grupo (nó sem localId) em que estava, se estava. */
  categoria: string | null
}

function clonar(n: SpatialTreeItem): SpatialTreeItem {
  return { ...n, children: n.children?.map(clonar) }
}

function indexar(raiz: SpatialTreeItem): Map<number, Posicao> {
  const out = new Map<number, Posicao>()
  const andar = (n: SpatialTreeItem, contentor: number | null) => {
    for (const filho of n.children ?? []) {
      if (filho.localId != null) {
        if (!out.has(filho.localId)) {
          out.set(filho.localId, {
            no: filho,
            pai: n,
            contentor,
            categoria: n.localId == null ? n.category ?? null : filho.category ?? null,
          })
        }
        andar(filho, filho.localId)
      } else {
        andar(filho, contentor)
      }
    }
  }
  andar(raiz, raiz.localId ?? null)
  return out
}

function tirar(p: Posicao): void {
  p.pai.children = (p.pai.children ?? []).filter((c) => c !== p.no)
}

/** Grupos (nós sem localId) que ficaram vazios saem. */
function limparGruposVazios(n: SpatialTreeItem): void {
  if (!n.children) return
  for (const c of n.children) limparGruposVazios(c)
  n.children = n.children.filter((c) => c.localId != null || (c.children?.length ?? 0) > 0)
}

export function aplicarContencao(
  raizOriginal: SpatialTreeItem,
  referenciasPorContentor: Map<number, number[]>,
  contentorDe: Map<number, number>,
): ArvoreComReferencias {
  const raiz = clonar(raizOriginal)
  const referenciados = new Map<number, Referenciado[]>()
  if (referenciasPorContentor.size === 0) return { raiz, referenciados }
  const pos = indexar(raiz)

  for (const [contentor, refs] of referenciasPorContentor) {
    for (const e of refs) {
      const real = contentorDe.get(e)
      // Contido e referenciado no mesmo contentor: nada a marcar.
      if (real === contentor) continue
      const p = pos.get(e)
      const categoria = p?.categoria ?? p?.no.category ?? null
      const lista = referenciados.get(contentor) ?? []
      lista.push({ localId: e, category: categoria })
      referenciados.set(contentor, lista)
      if (!p || p.contentor !== contentor) continue
      // O motor pô-lo debaixo de quem o referencia: tira-se dali e vai
      // para o contentor que o contém, se existir na árvore.
      tirar(p)
      const destino = real !== undefined ? pos.get(real)?.no : undefined
      if (!destino) continue
      const grupos = (destino.children ??= [])
      let grupo = grupos.find((g) => g.localId == null && g.category === categoria)
      if (!grupo) {
        grupo = { category: categoria, localId: null, children: [] }
        grupos.push(grupo)
      }
      ;(grupo.children ??= []).push(p.no)
      pos.set(e, { no: p.no, pai: grupo, contentor: real!, categoria })
    }
  }
  limparGruposVazios(raiz)
  return { raiz, referenciados }
}

/** Os localIds dos contentores espaciais da árvore, para se pedir ao
 *  motor o que cada um referencia. */
export function contentoresDaArvore(raiz: SpatialTreeItem, ehContentor: (categoria: string) => boolean): number[] {
  const out: number[] = []
  const andar = (n: SpatialTreeItem, categoriaDoGrupo: string | null) => {
    const cat = (n.category ?? categoriaDoGrupo ?? '').toUpperCase()
    if (n.localId != null && ehContentor(cat)) out.push(n.localId)
    for (const c of n.children ?? []) andar(c, n.localId == null ? n.category ?? null : null)
  }
  andar(raiz, null)
  return out
}

function idDe(v: unknown): number | null {
  if (!v || typeof v !== 'object') return null
  const o = v as { _localId?: unknown }
  const l = o._localId
  if (typeof l === 'number') return l
  if (l && typeof l === 'object' && typeof (l as { value?: unknown }).value === 'number') {
    return (l as { value: number }).value
  }
  return null
}

interface ModeloDaArvore {
  getItemsData(ids: number[], config: Record<string, unknown>): Promise<unknown[]> | unknown[]
}

/** Lê do motor o que cada contentor referencia (ReferencesElements) e o
 *  contentor real de cada referenciado (ContainedInStructure). Dois
 *  pedidos pequenos: só os contentores, e só os referenciados. */
export async function lerReferencias(
  modelo: ModeloDaArvore,
  contentores: number[],
): Promise<{ referenciasPorContentor: Map<number, number[]>; contentorDe: Map<number, number> }> {
  const referenciasPorContentor = new Map<number, number[]>()
  const contentorDe = new Map<number, number>()
  if (contentores.length === 0) return { referenciasPorContentor, contentorDe }
  const so = (rel: string) => ({
    attributesDefault: false,
    relationsDefault: { attributes: false, relations: false },
    relations: { [rel]: { attributes: true, relations: false } },
  })
  // Um pedido por contentor: num pedido só, o motor guarda cada
  // entidade na primeira forma em que a lê, e uma zona referenciada no
  // piso vinha rasa (sem as suas referências) por ter sido lida antes
  // como relação do piso. Os contentores são poucos.
  const dados = await Promise.all(
    contentores.map(async (id) => (await modelo.getItemsData([id], so('ReferencesElements')))[0]),
  )
  dados.forEach((d, i) => {
    const refs = (d as { ReferencesElements?: unknown[] } | null)?.ReferencesElements
    if (!Array.isArray(refs)) return
    const ids = refs.map(idDe).filter((x): x is number => x !== null)
    if (ids.length > 0) referenciasPorContentor.set(contentores[i], ids)
  })
  const todos = [...new Set([...referenciasPorContentor.values()].flat())]
  if (todos.length === 0) return { referenciasPorContentor, contentorDe }
  const deles = await modelo.getItemsData(todos, so('ContainedInStructure'))
  deles.forEach((d, i) => {
    const c = (d as { ContainedInStructure?: unknown[] } | null)?.ContainedInStructure
    const id = Array.isArray(c) ? idDe(c[0]) : null
    if (id !== null) contentorDe.set(todos[i], id)
  })
  return { referenciasPorContentor, contentorDe }
}
